import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { handleApi } from "../../apis";
import { GET_RECHARGE_CUSTOMERS_V3 } from "../../apis/configuration";
import { useSnackbar } from "../../context/SnackbarContext";
import useDebouncedValue from "../useDebouncedValue";

const PAGE_LIMIT = 20;

const normalizeContact = (c) => ({
  customer_id: c.customer_id || c.id || null,
  display_name: c.customer_name || c.name || null,
  customer_phone: c.customer_phone || c.phone || c.mobile || null,
  has_customer: Boolean(c.customer_name || c.name),
  total_txns: c.no_of_transactions || c.recharge_count || c.txn_count || c.total_txns || 0,
  recent_transactions: c.recent_transactions || null,
});

const useRechargeCustomers = ({ search = "", enabled = true, cacheRef = null } = {}) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const cache = cacheRef?.current;
  const hasCached = Boolean(cache?.contacts?.length);

  const [contacts, setContacts] = useState(hasCached ? cache.contacts : []);
  const [hasNext, setHasNext] = useState(hasCached ? cache.hasNext : false);
  const [totalCount, setTotalCount] = useState(hasCached ? cache.totalCount : 0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!hasCached);
  const [isError, setIsError] = useState(false);

  const cancelToken = useRef();
  const contactsRef = useRef(hasCached ? cache.contacts : []);
  const lastFetchedSearchRef = useRef(hasCached ? cache.search : null);
  const nextIdRef = useRef(hasCached ? (cache.next_id ?? null) : null);
  const nextLastTxnAtRef = useRef(hasCached ? (cache.next_last_transaction_at ?? null) : null);
  const pageRef = useRef(hasCached ? (cache.page ?? 1) : 1);

  const debouncedSearch = useDebouncedValue(search, 500);

  const fetchContacts = useCallback(async ({ search: s, reset }) => {
    if (cancelToken.current)
      cancelToken.current.cancel("New request appears, canceling previous request.");
    cancelToken.current = axios.CancelToken.source();

    setIsLoading(true);
    let canceled = false;
    let failed = false;
    try {
      const params = { page: pageRef.current, limit: PAGE_LIMIT };
      if (s) params.search = s;
      if (!reset && nextIdRef.current != null) params.next_id = nextIdRef.current;
      if (!reset && nextLastTxnAtRef.current != null) params.next_last_transaction_at = nextLastTxnAtRef.current;

      const data = await handleApi(GET_RECHARGE_CUSTOMERS_V3, params, cancelToken.current, navigate);

      if (data?.success) {
        const incoming = (data.customers || data.contacts || data.results || []).map(normalizeContact);
        const nextHasNext = Boolean(data.has_next || data.next_page);
        const nextTotal = reset ? (data.total_count || data.count || 0) : undefined;
        nextIdRef.current = data.next_id != null ? data.next_id : null;
        nextLastTxnAtRef.current = data.next_last_transaction_at != null ? data.next_last_transaction_at : null;
        setContacts((prev) => {
          const next = reset ? incoming : [...prev, ...incoming];
          contactsRef.current = next;
          if (cacheRef) {
            cacheRef.current = {
              contacts: next,
              hasNext: nextHasNext,
              next_id: nextIdRef.current,
              next_last_transaction_at: nextLastTxnAtRef.current,
              page: pageRef.current,
              totalCount: nextTotal ?? cacheRef.current?.totalCount ?? 0,
              search: s,
            };
          }
          return next;
        });
        setHasNext(nextHasNext);
        if (reset && nextTotal !== undefined) setTotalCount(nextTotal);
      } else {
        failed = true;
      }
    } catch (error) {
      if (error.message === "New request appears, canceling previous request.") {
        canceled = true;
        return;
      }
      failed = true;
      showSnackbar({ message: error?.response?.data?.detail || error?.response?.data?.message || "Error fetching contacts", severity: "error" });
    } finally {
      setIsLoading(false);
      // Don't hide the spinner if this request was canceled — the new request is still in flight
      if (!canceled) {
        setIsInitialLoading(false);
        setIsError(failed);
      }
    }
  }, [navigate, showSnackbar]);

  const resetAndFetch = useCallback((s) => {
    lastFetchedSearchRef.current = s;
    contactsRef.current = [];
    nextIdRef.current = null;
    nextLastTxnAtRef.current = null;
    pageRef.current = 1;
    setContacts([]);
    setHasNext(false);
    setIsInitialLoading(true);
    fetchContacts({ search: s, reset: true });
  }, [fetchContacts]);

  useEffect(() => {
    if (!enabled) return;
    if (contactsRef.current.length > 0 && debouncedSearch === lastFetchedSearchRef.current) return;
    resetAndFetch(debouncedSearch);
  }, [debouncedSearch, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = useCallback(() => {
    if (!hasNext || isLoading) return;
    pageRef.current += 1;
    fetchContacts({ search: debouncedSearch, reset: false });
  }, [hasNext, isLoading, debouncedSearch, fetchContacts]);

  return {
    contacts,
    hasNext,
    totalCount,
    isLoading,
    isInitialLoading,
    isError,
    handleLoadMore,
    resetAndFetch,
  };
};

export default useRechargeCustomers;
